import { WrtLevelLoggersBoardConditionEnum, WrtRainfallEnum, } from "@cartographerio/inventory-enums";
import { attachmentField, columns2, customRule, featureField, featureFieldCartographerMapStyle, form, minValue, numberField, page, required, requiredIff, section, select, textArea, timestampField, topoExpr, vstack, } from "@cartographerio/topo-form";
import { stringAttr } from "@cartographerio/topo-map";
import { unsafeMapLayerId } from "@cartographerio/types";
import { checkExhausted } from "@cartographerio/util";
import { outdent } from "outdent";
function booleanSelect(props) {
    return select({
        ...props,
        options: [
            { value: true, label: "Yes" },
            { value: false, label: "No" },
        ],
    });
}
const requiredIfBoardAccessible = requiredIff({
    requiredLevel: "error",
    requiredMessage: "You must specify a value.",
    forbiddenLevel: "info",
    forbiddenMessage: "Leave this blank when the board is inaccessible.",
    otherPath: ["data", "boardAccessible"],
    otherTest: value => value === true,
});
export default form({
    title: "Westcountry CSI",
    pages: [
        page({
            title: null,
            path: [],
            blocks: [
                section({
                    title: "Survey Details",
                    path: [],
                    blocks: [
                        columns2(vstack(booleanSelect({
                            label: "Dynamic Risk Assessment Completed/Reviewed?",
                            path: ["data", "dynamicRiskAssessment"],
                            required: required("error"),
                        }), timestampField({
                            label: "Date and Time",
                            path: ["data", "recorded"],
                            defaultValue: "now",
                            required: required("error"),
                            help: "When was the data collected in the field?",
                        }))),
                    ],
                }),
                section({
                    title: "Stage Board",
                    path: [],
                    blocks: [
                        featureField({
                            label: "Location",
                            path: ["data", "site"],
                            required: required("error"),
                            fullWidth: true,
                            mapOptions: {
                                mapStyle: featureFieldCartographerMapStyle({
                                    layer: unsafeMapLayerId("WrtLevelLoggersSite"),
                                    geometryType: "Point",
                                    primaryKey: "wiskiID",
                                }),
                                selectMinZoom: 12,
                                attributeColumns: 2,
                                attributes: [
                                    stringAttr({
                                        attributeId: "label",
                                        label: "Site Name",
                                        buckets: "auto",
                                    }),
                                    stringAttr({
                                        attributeId: "wiskiID",
                                        label: "Site ID",
                                        buckets: "auto",
                                    }),
                                ],
                            },
                            help: "Click one of the blue circles on the map to select a level station.",
                        }),
                        attachmentField({
                            label: "Photograph",
                            path: ["data", "photographs"],
                            maxFiles: 1,
                            fullWidth: false,
                            help: outdent `
              Ensuring that your phone is held in a landscape position (on its side),
              please take and upload one photo of the gauging board, so that the level of the water is clearly shown.
              See the images below as examples of good and bad practice:

              ![Examples of good and bad photograph practice](https://media.cartographer.io/static/images/wrt-level-loggers/photo-examples.jpg)
              `,
                        }),
                        columns2(vstack(booleanSelect({
                            label: "Board Accessible?",
                            path: ["data", "boardAccessible"],
                            required: required("error"),
                        }), select({
                            label: "Board Condition (select one)",
                            path: ["data", "boardCondition"],
                            options: boardConditionOptions(),
                            customRules: requiredIfBoardAccessible,
                        }))),
                    ],
                }),
                section({
                    title: "Water Level",
                    path: [],
                    blocks: [
                        columns2(vstack(numberField({
                            label: "Estimated Level (in Metres)",
                            path: ["data", "estimatedLevel"],
                            units: "m",
                            decimalPlaces: 2,
                            bounds: minValue(0, "error"),
                            customRules: [
                                ...requiredIfBoardAccessible,
                                customRule({
                                    level: "info",
                                    triggerWhen: topoExpr(env => {
                                        const value = env.getFocused();
                                        return typeof value === "number" && value >= 3;
                                    }),
                                    message: "This looks quite deep. Make sure you're recording in metres!",
                                }),
                            ],
                            help: outdent `
                    Record the level in metres to 2 decimal places.
                    For example, record '1m 20cm' as '1.20'.

                    To read off a stage board, use the black rectangles as a marker for how high the level is.
                    Each black rectangle represent 1cm graduations.
                    The bottom of the first black rectangle is 1cm level, the top of it equals 2cm and so on.
                  `,
                        }), select({
                            label: "Rain in Previous 24 Hours (select one)",
                            path: ["data", "recentRain"],
                            options: WrtRainfallEnum.entries,
                            required: required("error"),
                        }))),
                    ],
                }),
                section({
                    title: "Notes",
                    path: [],
                    help: outdent `
            ⚠️ **If you spot signs of pollution, damage or danger to the natural environment,**
            such as dead fish or fish gasping for air, call the Environment Agency 24-hour incident hotline **0800 80 70 60**.

            ⚠️ **If you spot anything you think could be a sewage leak/pollution across Devon or Cornwall,**
            call South West Water 24-hour hotline on **0344 346 2020**. This is manned 24-hours.

            Please note any incident reference number(s) you are given in the field below.
          `,
                    blocks: [
                        textArea({
                            label: null,
                            path: ["data", "notes"],
                            fullWidth: true,
                            rows: 5,
                        }),
                    ],
                }),
            ],
        }),
    ],
});
function boardConditionOptions() {
    return WrtLevelLoggersBoardConditionEnum.values.map(value => {
        switch (value) {
            case "Good":
                return {
                    value,
                    label: "Good - clearly visible, clean, and straight",
                };
            case "Fair":
                return {
                    value,
                    label: "Fair - Level, readable but board dirty / in need of repair",
                };
            case "Poor":
                return {
                    value,
                    label: "Poor - Board present but unreadable due to dirt / damage",
                };
            default:
                return { value, label: checkExhausted(value) };
        }
    });
}
//# sourceMappingURL=form.js.map